import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { fetchAllMembersDirectory } from '../api/membersDirectory';
import { MemberCard } from '../components/MemberCard';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import {
    getMemberGivenNameLetter,
    MEMBER_ALPHABET_LETTERS,
    MEMBERS,
    normalizeMemberSearchText,
    sortClubMembersByGivenName,
} from '../data/members';

const SEARCH_DEBOUNCE_MS = 350;

const MEMBER_SKELETON_ROWS = 5;

/**
 * @param {{ statusLabel: string }} props
 */
function MembersListSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <ul className="members-list members-list--skeleton list-unstyled mb-0">
                    {Array.from({ length: MEMBER_SKELETON_ROWS }, (_, i) => (
                        <li key={`member-sk-${i}`}>
                            <article
                                className="member-card member-card--skeleton"
                                aria-hidden
                            >
                                <div className="member-card-photos">
                                    <Skeleton
                                        width={68}
                                        height={68}
                                        borderRadius={8}
                                    />
                                    <Skeleton
                                        width={68}
                                        height={68}
                                        borderRadius={8}
                                    />
                                </div>
                                <div className="member-card-names">
                                    <Skeleton
                                        height={20}
                                        width="88%"
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={15}
                                        width="58%"
                                        borderRadius={6}
                                    />
                                </div>
                                <dl className="member-card-meta member-card-meta--skeleton">
                                    <div className="member-card-meta-item">
                                        <Skeleton
                                            height={38}
                                            width="100%"
                                            borderRadius={6}
                                        />
                                    </div>
                                    <div className="member-card-meta-item">
                                        <Skeleton
                                            height={38}
                                            width="100%"
                                            borderRadius={6}
                                        />
                                    </div>
                                    <div className="member-card-meta-item">
                                        <Skeleton
                                            height={38}
                                            width="100%"
                                            borderRadius={6}
                                        />
                                    </div>
                                    <div className="member-card-meta-item member-card-meta-item--email">
                                        <Skeleton
                                            height={38}
                                            width="100%"
                                            borderRadius={6}
                                        />
                                    </div>
                                </dl>
                            </article>
                        </li>
                    ))}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

function clientTextFilter(rows, queryTrim) {
    if (!queryTrim) return rows;
    const q = normalizeMemberSearchText(queryTrim);
    return rows.filter((m) => {
        const parts = [
            m.givenName,
            m.familyName,
            m.partnerGivenName,
            m.partnerFamilyName,
            m.profession,
            m.email,
            m.company,
            m.sector,
        ].filter(Boolean);
        const blob = normalizeMemberSearchText(parts.join(' '));
        return blob.includes(q);
    });
}

/**
 * Üye rehberi — URL: /member-area/members
 * Tam liste: `GET .../Member?pageNumber=1&pageSize=500`.
 * Arama: yüklü liste üzerinde istemci tarafı (isim, meslek, sektör, e-posta, …).
 * Simülasyonda aynı filtre kullanılır.
 */
export function MembersPage() {
    const { t } = useTranslation();
    const { authToken, isSimulatedAuth } = useAuth();
    const [letterFilter, setLetterFilter] = useState(
        /** @type {string | null} */ (null),
    );
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sourceMembers, setSourceMembers] = useState(
        /** @type {typeof MEMBERS | null} */ (null),
    );
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */ (null),
    );
    const skipLetterScrollRef = useRef(true);

    useEffect(() => {
        if (skipLetterScrollRef.current) {
            skipLetterScrollRef.current = false;
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [letterFilter]);

    useEffect(() => {
        const timer = window.setTimeout(
            () => setDebouncedSearch(search.trim()),
            SEARCH_DEBOUNCE_MS,
        );
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;

        if (!authToken || isSimulatedAuth) {
            setSourceMembers(MEMBERS);
            setLoadError(null);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            try {
                const list = await fetchAllMembersDirectory(authToken);
                if (cancelled) return;
                setSourceMembers(list);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                setSourceMembers(MEMBERS);
                setLoadError(
                    err instanceof Error
                        ? err.message
                        : t('membersPage.genericDirectoryError'),
                );
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authToken, isSimulatedAuth, t]);

    const searchTrim = search.trim();
    const searchPending = Boolean(searchTrim && searchTrim !== debouncedSearch);

    const baseList = useMemo(() => {
        if (sourceMembers !== null) return sourceMembers;
        if (!authToken || isSimulatedAuth) return MEMBERS;
        return [];
    }, [sourceMembers, authToken, isSimulatedAuth]);

    const filtered = useMemo(() => {
        let rows = clientTextFilter(baseList, debouncedSearch);

        if (letterFilter) {
            rows = rows.filter(
                (m) =>
                    getMemberGivenNameLetter(m.givenName) === letterFilter,
            );
        }
        return sortClubMembersByGivenName(rows);
    }, [baseList, debouncedSearch, letterFilter]);

    const directoryLoading =
        sourceMembers === null && Boolean(authToken) && !isSimulatedAuth;
    const searchBusy = searchPending;

    return (
        <>
            <PageSeo
                title={`${t('membersPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.membersDirectory')}
                noindex
            />
            <SubpageLayout
                title={t('membersPage.title')}
                subtitle={t('membersPage.subtitle')}
                contentClassName="members-page"
            >
            {loadError ? (
                <p
                    className="text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('membersPage.loadErrorNotice', { error: loadError })}
                </p>
            ) : null}
            <div className="members-toolbar">
                <div className="members-search-wrap">
                    <Search
                        className="members-search-icon"
                        size={18}
                        strokeWidth={1.85}
                        aria-hidden
                    />
                    <input
                        type="search"
                        className="form-control members-search-input"
                        placeholder={t('membersPage.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label={t('membersPage.searchAriaLabel')}
                        disabled={directoryLoading}
                    />
                </div>
                <p className="members-count mb-0" role="status">
                    {directoryLoading ? (
                        <>{t('membersPage.statusDirectoryLoading')}</>
                    ) : searchBusy ? (
                        <>{t('membersPage.statusSearching')}</>
                    ) : (
                        <>
                            {t('membersPage.countListed', {
                                count: filtered.length,
                            })}
                            {debouncedSearch && baseList.length !== filtered.length ? (
                                <>
                                    {t('membersPage.countFromRecordsSuffix', {
                                        total: baseList.length,
                                    })}
                                </>
                            ) : null}
                        </>
                    )}
                </p>
            </div>

            <div
                className="members-alphabet"
                role="toolbar"
                aria-label={t('membersPage.alphabetToolbarAria')}
            >
                <button
                    type="button"
                    className={`members-alphabet-btn${letterFilter === null ? ' is-active' : ''}`}
                    onClick={() => setLetterFilter(null)}
                    disabled={directoryLoading}
                >
                    {t('membersPage.allLetters')}
                </button>
                {MEMBER_ALPHABET_LETTERS.map((L) => (
                    <button
                        key={L}
                        type="button"
                        className={`members-alphabet-btn${letterFilter === L ? ' is-active' : ''}`}
                        onClick={() =>
                            setLetterFilter((cur) => (cur === L ? null : L))
                        }
                        disabled={directoryLoading}
                    >
                        {L}
                    </button>
                ))}
            </div>

            {directoryLoading ? (
                <MembersListSkeleton
                    statusLabel={t('membersPage.listLoadingServer')}
                />
            ) : searchBusy ? (
                <MembersListSkeleton
                    statusLabel={t('membersPage.statusSearching')}
                />
            ) : filtered.length === 0 ? (
                <p className="members-empty text-secondary mb-0">
                    {t('membersPage.emptyFiltered')}
                </p>
            ) : (
                <ul className="members-list list-unstyled mb-0">
                    {filtered.map((member) => (
                        <li key={member.id}>
                            <MemberCard member={member} />
                        </li>
                    ))}
                </ul>
            )}
        </SubpageLayout>
        </>
    );
}
